// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
// The reads and writes of Election Event > Signatures. Each signing table is
// selected, and each signing action sent, with its own permission as the Hasura
// role, so staff without admin-user don't get the portal's read-only default;
// Posts (elections), countries (areas) and people come from the portal's
// existing lists.
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import { useGetList, useNotify } from "react-admin";
import { useTranslation } from "react-i18next";
import { AuthContext } from "@/providers/AuthContextProvider";
import { useTenantStore } from "@/providers/TenantContextProvider";
import { useAliasRenderer } from "@/hooks/useAliasRenderer";
import { IPermissions } from "@/types/keycloak";
import { SIGNING_ACTIONS, SigningAction, SigningScope, } from "@/lib/signing/types";
import { GET_SIGNING_CERTIFICATES, GET_SIGNING_REQUESTS, GET_SIGNING_RULES, GET_SIGNING_RULE_CAPACITIES, GET_WAITING_SIGNING_REQUESTS, SIGNING_EVENT_INFO, SIGNING_DELETE_ISSUER, SIGNING_EXPORT_REQUESTS, SIGNING_IMPORT_ISSUERS, SIGNING_PUT_CHECKS, SIGNING_PUT_RULE, SIGNING_REGISTER_CERTIFICATE, SIGNING_REVOKE_CERTIFICATE, } from "@/queries/SigningSettings";
import { errorKey, signaturesAccess, signingError } from "./signingSettings";
/** Every list of the tab fits in one page. */
var ALL = { page: 1, perPage: 1000 };
var asRole = function (role) { return ({ headers: { "x-hasura-role": role } }); };
/** What the signed-in user may see and change in the tab. */
export function useSignaturesAccess() {
    var auth = useContext(AuthContext);
    var tenantId = useTenantStore()[0];
    return useMemo(function () { return signaturesAccess(function (permission) { return auth.isAuthorized(true, tenantId, permission); }); }, [auth, tenantId]);
}
export function useSigningRules(electionEventId) {
    var _a = useQuery(GET_SIGNING_RULES, {
        variables: { electionEventId: electionEventId },
        context: asRole(IPermissions.SIGNING_RULES_READ),
        fetchPolicy: "network-only",
    }), data = _a.data, loading = _a.loading, error = _a.error, refetch = _a.refetch;
    return { rules: data === null || data === void 0 ? void 0 : data.sequent_backend_signing_rule, loading: loading, error: error, refetch: refetch };
}
/** The alias of an action's capacity in GetSigningRuleCapacities. */
export var capacityAlias = function (action) { return action.replace(/-/g, "_"); };
export function useRuleCapacities(electionEventId) {
    var _a;
    var _b = useQuery(GET_SIGNING_RULE_CAPACITIES, {
        variables: { electionEventId: electionEventId },
        context: asRole(IPermissions.SIGNING_RULES_READ),
        fetchPolicy: "network-only",
    }), data = _b.data, error = _b.error, refetch = _b.refetch;
    var capacityOf = useCallback(function (action) { var _a; return (_a = data === null || data === void 0 ? void 0 : data[capacityAlias(action)]) !== null && _a !== void 0 ? _a : undefined; }, [data]);
    // Every action's answer carries the event's configuration version.
    var configVersion = (_a = Object.values(SigningAction)
        .map(function (action) { var _a; return (_a = data === null || data === void 0 ? void 0 : data[capacityAlias(action)]) === null || _a === void 0 ? void 0 : _a.config_version; })
        .find(function (version) { return version !== undefined && version !== null; })) !== null && _a !== void 0 ? _a : null;
    return { capacityOf: capacityOf, configVersion: configVersion, loaded: !!data, error: error, refetch: refetch };
}
export function useSigningCertificates(electionEventId) {
    var _a = useQuery(GET_SIGNING_CERTIFICATES, {
        variables: { electionEventId: electionEventId },
        context: asRole(IPermissions.SIGNING_CERTIFICATES_READ),
        fetchPolicy: "network-only",
    }), data = _a.data, loading = _a.loading, error = _a.error, refetch = _a.refetch;
    return { data: data, loading: loading, error: error, refetch: refetch };
}
export function useSigningRequests(electionEventId) {
    var _a = useQuery(GET_SIGNING_REQUESTS, {
        variables: { electionEventId: electionEventId },
        context: asRole(IPermissions.SIGNING_REQUESTS_READ),
        fetchPolicy: "network-only",
    }), data = _a.data, loading = _a.loading, error = _a.error;
    return { requests: data === null || data === void 0 ? void 0 : data.sequent_backend_signing_request, loading: loading, error: error };
}
/**
 * The sign permissions whose requests a signer finds in Hasura: every
 * action's but a trustee's, whose requests the trustee's ceremony step reads
 * through Harvest.
 */
export var LISTED_SIGN_PERMISSIONS = Object.values(SIGNING_ACTIONS)
    .filter(function (_a) {
    var scope = _a.scope;
    return scope !== SigningScope.Trustee;
})
    .map(function (_a) {
    var signPermission = _a.signPermission;
    return signPermission;
});
/** The permissions the user holds in the selected tenant. */
var useHolds = function () {
    var auth = useContext(AuthContext);
    var tenantId = useTenantStore()[0];
    return useCallback(function (permission) { return auth.isAuthorized(true, tenantId, permission); }, [auth, tenantId]);
};
/**
 * The event's requests waiting for signatures of the actions the user may
 * sign, in their Posts, oldest first: one query per `sign-<action>` they
 * hold, each as that role (its select permission keeps to its action).
 */
/** The listed sign permissions the user holds ([`LISTED_SIGN_PERMISSIONS`]). */
export function useListedSignPermissions() {
    var holds = useHolds();
    return useMemo(function () { return LISTED_SIGN_PERMISSIONS.filter(holds); }, [holds]);
}
export function useWaitingSigningRequests(electionEventId) {
    var client = useApolloClient();
    var roles = useListedSignPermissions();
    var _a = useState(null), requests = _a[0], setRequests = _a[1];
    var _b = useState(false), error = _b[0], setError = _b[1];
    var _c = useState(0), version = _c[0], setVersion = _c[1];
    var reload = useCallback(function () { return setVersion(function (previous) { return previous + 1; }); }, []);
    useEffect(function () {
        var current = true;
        Promise.all(roles.map(function (role) {
            return client.query({
                query: GET_WAITING_SIGNING_REQUESTS,
                variables: { electionEventId: electionEventId },
                // The same query as each role: never merged in flight nor in the cache.
                context: __assign(__assign({}, asRole(role)), { queryDeduplication: false }),
                fetchPolicy: "no-cache",
            });
        })).then(function (answers) {
            var _a;
            if (!current)
                return;
            var byId = new Map();
            for (var _i = 0, answers_1 = answers; _i < answers_1.length; _i++) {
                var data = answers_1[_i].data;
                for (var _b = 0, _c = (_a = data === null || data === void 0 ? void 0 : data.sequent_backend_signing_request) !== null && _a !== void 0 ? _a : []; _b < _c.length; _b++) {
                    var request = _c[_b];
                    byId.set(request.id, request);
                }
            }
            setError(false);
            setRequests(Array.from(byId.values()).sort(function (a, b) {
                return a.created_at.localeCompare(b.created_at);
            }));
        }, function () { return current && setError(true); });
        return function () {
            current = false;
        };
    }, [client, electionEventId, roles, version]);
    return { requests: requests, error: error, roles: roles, reload: reload };
}
/** The roles that read the event's signing information, the one that also gets the titles first. */
var EVENT_INFO_ROLES = __spreadArray([
    IPermissions.SIGNING_CERTIFICATES_READ,
    IPermissions.SIGNING_REQUESTS_READ,
    IPermissions.SIGNING_RULES_READ
], Object.values(SIGNING_ACTIONS).map(function (_a) {
    var signPermission = _a.signPermission;
    return signPermission;
}), true);
/**
 * The event's time zone, which the tab and a signer's list show times in
 * (as the signing panel does), and the signers' titles for a reader of the
 * certificates. `timeZone` is null until known, or without such a permission.
 */
export function useSigningEventInfo(electionEventId) {
    var _a, _b, _c, _d;
    var holds = useHolds();
    var role = EVENT_INFO_ROLES.find(holds);
    var data = useQuery(SIGNING_EVENT_INFO, {
        variables: { electionEventId: electionEventId },
        context: role ? asRole(role) : undefined,
        skip: !role,
    }).data;
    return {
        timeZone: (_b = (_a = data === null || data === void 0 ? void 0 : data.signingEventInfo) === null || _a === void 0 ? void 0 : _a.time_zone) !== null && _b !== void 0 ? _b : null,
        titles: (_d = (_c = data === null || data === void 0 ? void 0 : data.signingEventInfo) === null || _c === void 0 ? void 0 : _c.titles) !== null && _d !== void 0 ? _d : {},
    };
}
/** A signing action of the tab, sent with its own permission as the Hasura role. */
var useSigningWrite = function (mutation, permission) {
    return useMutation(mutation, { context: asRole(permission) });
};
export var usePutRule = function () {
    return useSigningWrite(SIGNING_PUT_RULE, IPermissions.SIGNING_RULES_WRITE);
};
export var useImportIssuers = function () {
    return useSigningWrite(SIGNING_IMPORT_ISSUERS, IPermissions.SIGNING_ISSUERS_WRITE);
};
export var useDeleteIssuer = function () {
    return useSigningWrite(SIGNING_DELETE_ISSUER, IPermissions.SIGNING_ISSUERS_WRITE);
};
export var usePutChecks = function () {
    return useSigningWrite(SIGNING_PUT_CHECKS, IPermissions.SIGNING_CHECKS_WRITE);
};
export var useRegisterCertificate = function () {
    return useSigningWrite(SIGNING_REGISTER_CERTIFICATE, IPermissions.SIGNING_CERTIFICATES_REGISTER);
};
export var useRevokeCertificate = function () {
    return useSigningWrite(SIGNING_REVOKE_CERTIFICATE, IPermissions.SIGNING_CERTIFICATES_REVOKE);
};
export var useExportRequests = function () {
    return useSigningWrite(SIGNING_EXPORT_REQUESTS, IPermissions.SIGNING_REQUESTS_EXPORT);
};
/** Names of the event's Posts (elections) and countries (areas); the id until they load. */
export function useScopeNames(electionEventId, withCountries) {
    if (withCountries === void 0) { withCountries = false; }
    var aliasRenderer = useAliasRenderer();
    var filter = { election_event_id: electionEventId };
    var elections = useGetList("sequent_backend_election", {
        filter: filter,
        pagination: ALL,
    }).data;
    var areas = useGetList("sequent_backend_area", { filter: filter, pagination: ALL }, { enabled: withCountries }).data;
    var postName = useCallback(function (electionId) {
        var election = elections === null || elections === void 0 ? void 0 : elections.find(function (_a) {
            var id = _a.id;
            return id === electionId;
        });
        return election ? aliasRenderer(election) : (electionId !== null && electionId !== void 0 ? electionId : "");
    }, [elections, aliasRenderer]);
    var countryName = useCallback(function (areaId) { var _a, _b, _c; return (_c = (_b = (_a = areas === null || areas === void 0 ? void 0 : areas.find(function (_a) {
        var id = _a.id;
        return id === areaId;
    })) === null || _a === void 0 ? void 0 : _a.name) !== null && _b !== void 0 ? _b : areaId) !== null && _c !== void 0 ? _c : ""; }, [areas]);
    return { elections: elections !== null && elections !== void 0 ? elections : [], postName: postName, countryName: countryName };
}
/** The tenant's users matching the typed username; nothing until something is typed. */
export function usePeopleSearch(search) {
    var tenantId = useTenantStore()[0];
    var username = search.trim();
    var _a = useGetList("user", { filter: { tenant_id: tenantId, username: username }, pagination: { page: 1, perPage: 20 } }, { enabled: username.length > 0 }), data = _a.data, isLoading = _a.isLoading;
    return { people: username ? (data !== null && data !== void 0 ? data : []) : [], loading: username.length > 0 && isLoading };
}
/**
 * Tells the user why a write was refused: the permission, the input, someone
 * else's save, the lockdown or a missing record; otherwise the write's own
 * localized fallback. Formats the explanation without a notification so forms
 * can show it once in an inline alert.
 */
export function useWriteErrorMessage() {
    var t = useTranslation().t;
    return useCallback(function (error, fallbackKey, _a) {
        var _b;
        var _c = _a === void 0 ? {} : _a, _d = _c.lockedDown, lockedDown = _d === void 0 ? false : _d;
        var refusal = signingError(error);
        return t((_b = errorKey(refusal.code, lockedDown, refusal.reason)) !== null && _b !== void 0 ? _b : fallbackKey);
    }, [t]);
}
/** Shows the localized write explanation when there is no inline error display. */
export function useWriteError() {
    var messageOf = useWriteErrorMessage();
    var notify = useNotify();
    return useCallback(function (error, fallbackKey, _a) {
        var _b = _a === void 0 ? {} : _a, _c = _b.lockedDown, lockedDown = _c === void 0 ? false : _c;
        var message = messageOf(error, fallbackKey, { lockedDown: lockedDown });
        notify(message, { type: "error" });
        return message;
    }, [messageOf, notify]);
}
